import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Users, 
  Truck, 
  X,
  Bot,
  Boxes,
  ClipboardList,
  ChevronDown,
  ChevronRight,
  PackageOpen,
  Package,
  Scissors,
  Factory,
  Wrench,
  BarChart,
  UserCog,
  ListTodo,
  Briefcase,
  MessageSquare,
  Clock,
  DollarSign,
  Wallet,
  Calculator,
  Settings,
  FileText
} from 'lucide-react';
import { useAppStore } from '../../store';
import { useAuth } from '../../context/AuthContext';

interface SidebarProps {
  isMobileOpen: boolean;
  toggleMobileSidebar: () => void;
}

interface MenuItem {
  name: string;
  icon?: React.ElementType;
  href?: string;
  items?: {
    name: string;
    href: string;
    icon: React.ElementType;
    roles?: string[];
  }[];
  roles?: string[];
}

const Sidebar: React.FC<SidebarProps> = ({ isMobileOpen, toggleMobileSidebar }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [openGroup, setOpenGroup] = useState<string | null>(null);
  const { darkMode } = useAppStore();
  const { userRole, isAdmin } = useAuth();
  
  const navigation: MenuItem[] = [
    {
      name: 'Gestión',
      icon: Briefcase,
      roles: ['Administrador', 'Producción', 'Vendedor'],
      items: [
        { name: 'Dashboard', href: '/', icon: LayoutDashboard, roles: ['Administrador'] },
        { name: 'Clientes', href: '/clientes', icon: Users, roles: ['Administrador', 'Vendedor'] },
        { name: 'Proveedores', href: '/proveedores', icon: Truck, roles: ['Administrador', 'Producción'] },
        { name: 'Facturas', href: '/facturas', icon: FileText, roles: ['Administrador'] },
        { name: 'Reportes', href: '/reportes', icon: BarChart, roles: ['Administrador'] },
        { name: 'Remitos', href: '/remitos', icon: FileText, roles: ['Administrador'] },
        { name: 'Producción', href: '/productionadmin', icon: BarChart, roles: ['Administrador'] },
        { name: 'Gastos', href: '/gastos', icon: DollarSign, roles: ['Administrador', 'Producción'] },
        { name: 'Tesorería', href: '/tesoreria', icon: Wallet, roles: ['Administrador'] },
        { name: 'Cotizador', href: '/cotizador', icon: Calculator, roles: ['Administrador', 'Vendedor'] },
        { name: 'Actividad', href: '/actividad', icon: Clock, roles: ['Administrador'] },
        { name: 'Asistente', href: '/asistente', icon: Bot, roles: ['Administrador'] },
        { name: 'Usuarios', href: '/usuarios', icon: UserCog, roles: ['Administrador'] },
        { name: 'To-Do Diario', href: '/todolist', icon: ListTodo, roles: ['Administrador'] },
      ]
    },
    {
      name: 'Depósito',
      icon: Package,
      roles: ['Administrador', 'Depósito'],
      items: [
        { name: 'Pedidos', href: '/pedidos', icon: Package, roles: ['Administrador', 'Depósito'] }
      ]
    },
    {
      name: 'Express',
      icon: PackageOpen,
      roles: ['Administrador', 'Vendedor', 'Producción', 'Depósito'],
      items: [
        { name: 'Producciones', href: '/express', icon: PackageOpen, roles: ['Administrador', 'Vendedor', 'Producción'] },
        { name: 'Muestras', href: '/express/muestras', icon: Scissors, roles: ['Administrador', 'Vendedor', 'Producción'] },
        { name: 'Stock', href: '/express/stock', icon: Package, roles: ['Administrador', 'Vendedor', 'Producción', 'Depósito'] },
      ]
    },
    {
      name: 'PRO',
      icon: Factory,
      roles: ['Administrador', 'Vendedor', 'Producción'],
      items: [
        { name: 'Producciones', href: '/producciones', icon: Boxes, roles: ['Administrador', 'Vendedor', 'Producción'] },
        { name: 'Muestras', href: '/muestras', icon: Scissors, roles: ['Administrador', 'Vendedor', 'Producción'] },
        { name: 'Cotizador', href: '/cotizador', icon: Calculator, roles: ['Administrador', 'Vendedor'] },
      ]
    },
    {
      name: 'Producción',
      icon: Wrench,
      roles: ['Administrador', 'Producción'],
      items: [
        { name: 'Manager', href: '/productionmanager', icon: Wrench, roles: ['Administrador', 'Producción'] },
        { name: 'Logística', href: '/logistics', icon: Truck, roles: ['Administrador', 'Producción'] },
      ]
    },
    {
      name: 'Configuración',
      icon: Settings,
      roles: ['Administrador'],
      items: [
        { name: 'General', href: '/configuracion', icon: Settings, roles: ['Administrador'] },
        { name: 'Cotizador', href: '/configuracion/cotizador', icon: Calculator, roles: ['Administrador'] },
      ]
    }
  ];

  const isActive = (path: string) => location.pathname === path;
  
  // Find which group should be open based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    
    // Find which group contains the current path
    for (const group of filteredNavigation) {
      if (group.items) {
        const matchingItem = group.items.find(item => item.href === currentPath);
        if (matchingItem) {
          setOpenGroup(group.name);
          break;
        }
      }
    }
  }, [location.pathname]);
  
  const toggleGroup = (groupName: string) => {
    // If clicking the same group, toggle it
    if (openGroup === groupName) {
      setOpenGroup(null);
    } else {
      // Otherwise, open the clicked group and close any other
      setOpenGroup(groupName);
    }
  };

  const isGroupOpen = (groupName: string) => openGroup === groupName;

  const handleNavigation = (href: string) => {
    navigate(href);
    if (isMobileOpen) {
      toggleMobileSidebar();
    }
  };

  // For administrators, show all menu items without filtering
  // For other roles, filter based on role permissions
  const getFilteredNavigation = () => {
    // Admin sees everything - no filtering
    if (isAdmin) {
      return navigation;
    }
    
    // For non-admin users, filter based on role
    return navigation
      .filter(group => {
        // If group has roles defined, check if user has one of those roles
        if (group.roles && userRole) {
          return group.roles.includes(userRole);
        }
        // If no roles defined for group, show to everyone
        return true;
      })
      .map(group => ({
        ...group,
        // Filter items within group based on user role
        items: group.items?.filter(item => {
          // If item has roles defined, check if user has one of those roles
          if (item.roles && userRole) {
            // Special case: Allow Producción role to access Proveedores
            if (userRole === 'Producción' && item.name === 'Proveedores') {
              return true;
            }
            return item.roles.includes(userRole);
          }
          // If no roles defined for item, show to everyone
          return true;
        })
      }))
      .filter(group => group.items && group.items.length > 0); // Remove empty groups
  };

  const filteredNavigation = getFilteredNavigation();

  return (
    <>
      {/* Mobile sidebar backdrop */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 z-20 bg-gray-900/50 lg:hidden" 
          onClick={toggleMobileSidebar}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed top-0 left-0 z-30 h-full w-64 bg-black text-white transform transition-transform duration-300 ease-in-out
        ${isMobileOpen ? 'translate-x-0' : '-translate-x-full'} 
        lg:translate-x-0 lg:static lg:z-0
      `}>
        <div className="flex items-center justify-between h-16 px-4 border-b border-gray-800">
          <div className="flex items-center">
            <span className="text-xl font-bold tracking-wider">PRODUCTEX</span>
          </div>
          <button 
            onClick={toggleMobileSidebar} 
            className="lg:hidden text-white"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <nav className="p-4 h-[calc(100vh-64px)] overflow-y-auto scrollbar-hide">
          <div className="space-y-2">
            {filteredNavigation.map((group) => (
              <div key={group.name} className="border-b border-gray-800 pb-2 last:border-0">
                <button
                  onClick={() => toggleGroup(group.name)}
                  className="flex items-center justify-between w-full px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors group"
                >
                  <div className="flex items-center">
                    <div className="p-1.5 bg-black rounded-lg mr-3">
                      {group.icon && <group.icon className="h-4 w-4 text-white" />}
                    </div>
                    <span className="font-semibold uppercase tracking-wider">
                      {group.name}
                    </span>
                  </div>
                  {group.items && group.items.length > 0 && (
                    <div className="text-gray-500 group-hover:text-white">
                      {isGroupOpen(group.name) ? (
                        <ChevronDown className="h-4 w-4" />
                      ) : (
                        <ChevronRight className="h-4 w-4" />
                      )}
                    </div>
                  )}
                </button>
                
                {group.items && group.items.length > 0 && isGroupOpen(group.name) && (
                  <ul className="mt-1 ml-4 space-y-1">
                    {group.items.map((item) => {
                      const active = isActive(item.href);
                      return (
                        <li key={item.name}>
                          <button
                            onClick={() => handleNavigation(item.href)}
                            className={`
                              flex items-center w-full px-4 py-1 text-sm rounded-md transition-colors text-left
                              ${active 
                                ? 'bg-gray-800 text-white' 
                                : 'text-gray-300 hover:bg-gray-800 hover:text-white'
                              }
                            `}
                          >
                            <div className="p-1.5 bg-black rounded-lg mr-3">
                              <item.icon className="h-4 w-4 text-white" />
                            </div>
                            {item.name}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            ))}
          </div>
        </nav>
      </div>
    </>
  );
};

export default Sidebar;